import { Spinner } from "@material-tailwind/react";
import { StyledSharedButton } from "../../../../styles";

interface ISharedButtonProps {
  isLoading?: boolean;
  text?: string;
  disabled?: boolean;
  className?: string;
  icon?:any;
  children?: React.ReactNode;
  onClick?: (...args: any) => void;
}

/**
 * @description
 * SharedButton component renders a button with customizable text, loading state, and styles.
 * It supports disabled state and an optional spinner when loading.
 *
 * @usage
 * ```tsx
 * <SharedButton
 *   isLoading={true}
 *   text="Submit"
 *   disabled={false}
 *   className="custom-class"
 *   onClick={handleClick}
 * >
 *   Click me
 * </SharedButton>
 * ```
 *
 * @param {ISharedButtonProps} props - The props for the SharedButton component.
 * @param {boolean} [props.isLoading=false] - Indicates if the button is in a loading state.
 * @param {string} [props.text="button"] - The text to display on the button.
 * @param {boolean} [props.disabled=false] - Indicates if the button is disabled.
 * @param {string} [props.className] - Additional class names for the button.
 * @param {React.ReactNode} [props.children] - The children elements to render inside the button.
 * @param {() => void} [props.onClick] - The function to call when the button is clicked.
 *
 * @returns {JSX.Element} The rendered button component.
 */
export const SharedButton = ({
  isLoading = false,
  text = "button",
  disabled = false,
  children,
  icon,
  onClick,
  className
}: ISharedButtonProps): JSX.Element => {
  return className ? (
    <StyledSharedButton
      className={`${className} ${disabled ? 'bg-gray-300' : ''}`}
      isLoading={isLoading}
      onClick={onClick}
      disabled={isLoading || disabled}
    >
      <>
       {icon ? (
        
           <>
             <span className="mr-1">
               <img src={icon} alt="Icon"  />
             </span>
             <span>{children || text}</span>
           </>
         )
        : (
       <> {children || text}</>
       )}
     </>
    </StyledSharedButton>
  ) : (
    <button
      className={`${className} ${disabled ? 'bg-gray-300' : ''}`}
      onClick={onClick}
      disabled={isLoading || disabled}
    >
      {isLoading ? <Spinner /> : children || text}
    </button>
  );
};
